import { Component, effect, inject, OnInit, OnDestroy, PLATFORM_ID, Input } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ModalService } from '../../../../services/modals/modal.service';
import { ApiService } from '../../../../services/api.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ICountry } from '../../../../models/ICountry';
import { IState } from '../../../../models/IState';
import { CustomValidators } from '../../../../shared/validators/custom-validators';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SanitizeHtmlTextPipe } from '../../../../shared/pipes/sanitize-html-text.pipe';
import { SponsorService } from '../../../../services/sponsors/sponsor.service';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';
import { BecomeSponsorSuccessPageComponent } from '../become-sponsor-success-page/become-sponsor-success-page.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { IBecomeSponsorRequest } from '../../../../models/IBecomeSponsorRequest';
import { NgxCaptchaModule } from 'ngx-captcha';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { RecaptchaLoaderService } from '../../../../services/recaptcha-loader.service';
import { environment } from '../../../../../environments/environment';
import { ThemeService } from '../../../../services/theme.service';


@Component({
  selector: 'app-become-sponsor-form',
  imports: [ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    FormsModule,
    SelectModule,
    ProgressSpinnerModule,
    SanitizeHtmlTextPipe
    , HeaderComponent,
    FooterComponent,
    NgxCaptchaModule,
    RouterModule],
  providers: [],
  templateUrl: './become-sponsor-form.component.html',
  styleUrl: './become-sponsor-form.component.scss'
})
export class BecomeSponsorFormComponent implements OnInit, OnDestroy {
  private readonly themeService = inject(ThemeService);
  private readonly platformId = inject(PLATFORM_ID);
  modalService = inject(ModalService);
  apiService = inject(ApiService);
  sponsorService = inject(SponsorService);
  private route = inject(ActivatedRoute);

  @Input() id!: number;

  readonly becomeSponsorTextSignal = this.sponsorService.becomeSponsorTextSignal;
  homePageSignal = this.apiService.homePageSignal;

  becomeSponsorForm: FormGroup

  countries: ICountry[] = [];
  states: IState[] = [];

  labelState!: string;
  labelPostalCode!: string;
  placeHolderState!: string;
  defaultCountry!: string;
  apiResponseError: string | null = null;


  // recaptcha set up
  siteKey!: string;
  useGlobalDomain!: boolean;
  isBrowser: boolean = false;

  private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
  private readonly recaptchaLoader = inject(RecaptchaLoaderService);

  constructor(private fb: FormBuilder) {
    this.becomeSponsorForm = this.fb.group({
      fullName: ['', [CustomValidators.required('Full Name')]],
      organization: [''],
      mobile: [''],
      email: ['', [CustomValidators.required('Email'), CustomValidators.email()]],
      address1: [''],
      address2: [''],
      country: [''],
      zip: [''],
      state: [''],
      city: [''],
      comment: ['']
    });

    effect(() => {
      const countries = this.apiService.countrySignal();
      if (countries)
        // console.log('countries', countries);
        this.countries = countries;

      const states = this.apiService.stateSignal();
      if (states && states.length > 0) {
        this.states = states;
        // this.creditCardInfoForm.get('state')?.setValue(this.states[0].abbreviation);
      } else {
        this.states = [];
        this.becomeSponsorForm.get('state')?.setValue('');
      }

    });
  }

  ngOnDestroy(): void {
    this.recaptchaLoader.unload();
  }

  ngOnInit(): void {
    this.recaptchaLoader.load();

    if (isPlatformBrowser(this.platformId)) {
      this.themeService.customizeAuctionTheme(this.id);
    }

    this.getBecomeSponsorText();

    this.onCountries();

    if (!this.apiService.homePageSignal()) {
      const endpoint = 'auctions';
      this.apiService.getAuctionHomePageData(endpoint, this.id!);
      const homePageData = this.apiService.homePageSignal()
      if (homePageData) {
        this.defaultCountry = this.homePageSignal()!.orgCountryCode;

      }
    } else {
      this.defaultCountry = this.homePageSignal()!.orgCountryCode;

    }

    this.becomeSponsorForm.get('country')?.setValue(this.defaultCountry);
    this.onLabelState(this.defaultCountry);
    this.onStates(this.defaultCountry);

    if (this.defaultCountry === 'US') {
      this.labelPostalCode = 'Zip Code'
    } else {
      this.labelPostalCode = 'Postal Code'

    }

    //country select event
    this.becomeSponsorForm.get('country')?.valueChanges.subscribe(country => {
      this.onLabelState(country);
      this.onStates(country);

      // Reset fields and clear validators 
      this.becomeSponsorForm.get('state')?.clearValidators();
      this.becomeSponsorForm.get('zipCode')?.clearValidators();
      this.becomeSponsorForm.get('state')?.reset();
      this.becomeSponsorForm.get('zipCode')?.reset();


      this.becomeSponsorForm.get('state')?.setValidators([
        CustomValidators.required(this.labelState),
        CustomValidators.atLeastOneLetter(this.labelState)
      ]);
      this.becomeSponsorForm.get('state')?.updateValueAndValidity();


      this.labelPostalCode = country === 'US' ? 'Zip Code' : 'Postal Code';

      this.becomeSponsorForm.get('zipCode')?.setValidators([
        CustomValidators.required(this.labelPostalCode),
        CustomValidators.zipCodeValidator(this.labelPostalCode)
      ]);
      this.becomeSponsorForm.get('zipCode')?.updateValueAndValidity();
    });
  }

  onLabelState(country: string) {
    switch (country) {
      case 'US':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      case 'CA':
        this.labelState = 'Province'
        this.placeHolderState = 'Select Province'
        break;
      case 'AU':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      default:
        this.labelState = 'Region'
        break;
    }
  }

  getFieldError(field: string): string | null {
    const control = this.becomeSponsorForm.get(field);
    if (control && control.errors && (control.dirty || control.touched)) {
      const firstErrorKey = Object.keys(control.errors)[0];
      return control.errors[firstErrorKey];
    }
    return null;
  }

  getBecomeSponsorText() {
    const id = this.id;
    if (id !== null) {
      this.sponsorService.getBecomeSponsorText(id).subscribe();
    }
  }

  onCountries() {
    if (typeof window !== 'undefined') {
      this.apiService.getCountries().subscribe();
    }
  }

  onStates(countrySelected: string) {
    if (typeof window !== 'undefined') {
      const form = this.becomeSponsorForm.value;
      // const countrySelected = form.get('country')?.value;

      this.apiService.getStates(countrySelected).subscribe();

    }
  }

  onSubmit() {

    if (this.becomeSponsorForm.invalid) {
      this.becomeSponsorForm.markAllAsTouched();
      Object.keys(this.becomeSponsorForm.controls).forEach(key => {
        const control = this.becomeSponsorForm.get(key);
        if (control && control.invalid) {
          console.log(`${key} is invalid`, control.errors);
        }
      });

      return;
    }


    const form = this.becomeSponsorForm.value;
    // console.log(environment.siteKey);
    this.recaptchaV3Service.execute(environment.siteKey, 'become_sponsor', (token: string) => {

      const becomeRequestObj: IBecomeSponsorRequest = {
        fullName: form.fullName,
        email: form.email,
        organization: form.organization,
        phone: form.phone,
        comments: form.comments,
        countryCode: form.country,
        address1: form.address1,
        address2: form.address2,
        zip: form.zip,
        city: form.city,
        region: form.region
      };

      const recaptchaDom = document.querySelector('.grecaptcha-badge') as HTMLElement;

      this.sponsorService.postBecomeASponsor(this.id, becomeRequestObj, token).subscribe({
        next: () => {
          recaptchaDom.style.visibility = 'visible';
          this.modalService.openModal('become-sponsor-success', BecomeSponsorSuccessPageComponent, {
            width: '460px',
            modal: true,
            closable: true,
            dismissableMask: true,
            contentStyle: {
              padding: '1.5rem',
              maxHeight: '90vh',
              overflowY: 'auto'
            },
            data: { auctionId: this.id }
          });

          setTimeout(() => {
            recaptchaDom.style.visibility = 'hidden';
          }, 3000);

          this.becomeSponsorForm.reset();
        },
        error: (err) => {
          let errorMsg = 'unknown error';

          //If it is a json string
          if (typeof err.error === 'string') {
            const parsed = JSON.parse(err.error);
            errorMsg = parsed.message || errorMsg;
          }//If it is an object
          else if (err.error && err.error.errorMessage) {
            errorMsg = err.error.errorMessage;
          }

          this.apiResponseError = 'Error: ' + errorMsg;
        }
      });
    });
  }
}
